import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

export default function CrudPage({ title, subtitle, endpoint, fields, testPrefix }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);

  const empty = Object.fromEntries(fields.map((f) => [f.k, f.type === "number" ? 0 : ""]));

  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [endpoint]);

  const save = async () => {
    try {
      if (editId) await api.patch(`${endpoint}/${editId}`, form);
      else await api.post(endpoint, form);
      toast.success("Salvo!"); setOpen(false); setForm(empty); setEditId(null); load();
    } catch { toast.error("Erro ao salvar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir?")) return;
    await api.delete(`${endpoint}/${id}`); load();
  };

  const edit = (item) => { setForm({ ...empty, ...item }); setEditId(item.id); setOpen(true); };
  const newItem = () => { setForm(empty); setEditId(null); setOpen(true); };

  return (
    <div className="space-y-6" data-testid={`${testPrefix}-page`}>
      <header className="flex justify-between items-end">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">{subtitle}</div>
          <h1 className="font-display text-4xl font-bold">{title}</h1>
        </div>
        <button onClick={newItem} data-testid={`${testPrefix}-new-btn`}
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2">
          <Plus size={16} /> Novo
        </button>
      </header>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              {fields.filter((f) => !f.hidden).map((f) => (
                <th key={f.k} className="text-left p-3">{f.label}</th>
              ))}
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`${testPrefix}-row-${i.id}`}>
                {fields.filter((f) => !f.hidden).map((f) => (
                  <td key={f.k} className="p-3">{i[f.k] || "—"}</td>
                ))}
                <td className="p-3 text-right">
                  <button onClick={() => edit(i)} data-testid={`${testPrefix}-edit-${i.id}`} className="p-1 hover:bg-slate-200 rounded-sm mr-1"><Pencil size={14} /></button>
                  <button onClick={() => remove(i.id)} data-testid={`${testPrefix}-delete-${i.id}`} className="p-1 hover:bg-red-100 text-red-500 rounded-sm"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={fields.length + 1} className="p-12 text-center text-slate-500">Nenhum registro.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid={`${testPrefix}-modal`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl font-bold">{editId ? "Editar" : "Novo"}</h2>
              <button onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              {fields.map((f) => (
                <div key={f.k}>
                  <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">{f.label}</label>
                  <input type={f.type || "text"} value={form[f.k] || ""}
                    onChange={(e) => setForm({...form, [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value})}
                    data-testid={`${testPrefix}-${f.k}-input`}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm">Cancelar</button>
              <button onClick={save} data-testid={`${testPrefix}-save-btn`} className="bg-[#2563EB] text-white px-4 py-2 rounded-sm text-sm font-semibold">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
